import { Box } from '@mui/system';
import React from 'react';
import AtlasSalesExportModal from 'src/components/AtlasV2Components/AtlasDorisExportComponents/AtlasSalesExportModal';
import {
  EXPORT_TYPES,
  EXPORT_TYPES_DROPDOWN_OPTIONS
} from 'src/components/AtlasV2Components/AtlasDorisExportComponents/Consts';
import AtlasV2MetricListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2MetricListContainer';
import AtlasV2ProductListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2ProductListContainer';
import {
  DorisGroupByFieldNameEnum,
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum,
  DorisTableFieldColumnNamesEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { usePaginatedProductData } from 'src/components/AtlasV2Components/utils/tableUtils';
import { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import { CONTENT_TABLE_HEADER_DISPLAY_NAMES } from 'src/components/BeaconV2Components/ExperimentalLayout/Content/constants';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { getWeekColumnDefinition } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { removeComparisonFormatter } from 'src/components/BeaconV2Components/MetricList/utils';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { getAtlasSalesV2Treatment } from 'src/utils/app';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';

const fields = [
  {
    name: DorisTableFieldColumnNamesEnum.ContentScore,
    displayName: CONTENT_TABLE_HEADER_DISPLAY_NAMES.CONTENT_SCORE
  },
  {
    name: DorisTableFieldColumnNamesEnum.TitleScore,
    displayName: CONTENT_TABLE_HEADER_DISPLAY_NAMES.TITLE_SCORE
  },
  {
    name: DorisTableFieldColumnNamesEnum.BulletScore,
    displayName: CONTENT_TABLE_HEADER_DISPLAY_NAMES.BULLET_SCORE
  },
  {
    name: DorisTableFieldColumnNamesEnum.ImageScore,
    displayName: CONTENT_TABLE_HEADER_DISPLAY_NAMES.IMAGE_SCORE
  },
  {
    name: DorisTableFieldColumnNamesEnum.VideoScore,
    displayName: CONTENT_TABLE_HEADER_DISPLAY_NAMES.VIDEO_SCORE
  },
  {
    name: DorisTableFieldColumnNamesEnum.AplusScore,
    displayName: CONTENT_TABLE_HEADER_DISPLAY_NAMES.APLUS_SCORE
  },
  {
    name: DorisTableFieldColumnNamesEnum.RetailSales,
    displayName: CONTENT_TABLE_HEADER_DISPLAY_NAMES.RETAIL_SALES
  }
];

/**
 * Shows the Content Score for each product in a grid,
 * sorted by units sold
 */
export default function AtlasContentV2ProductGrid() {
  const { isProductEntity } = useProductEntity();
  const {
    enrichedData,
    isLoading,
    isMetadataLoading,
    currentPage,
    handleNextPage,
    handlePreviousPage,
    getPagedValuesByFieldName,
    getPagedValueDifferencesByFieldName
  } = usePaginatedProductData({
    apiMethodName: DorisMetricEndPointEnum.SalesEndPoint,
    groupByFieldName: isProductEntity ? DorisGroupByFieldNameEnum.Week : DorisGroupByFieldNameEnum.Product,
    metricType: DorisMetricTypeEnum.ProductsWithContentScoreData,
    fields,
    sortFieldName: isProductEntity ? 'weekId' : 'retailSales',
    itemsPerPage: 20,
    metadataFetchSize: 100
  });

  return (
    <Box marginTop={`${BEACON_CHART_SPACING}px`}>
      {isProductEntity ? (
        <AtlasV2MetricListContainer
          entityTableHeaderProps={{
            title: 'Content Utilization by Week',
            customExportModal: (props: { open: boolean; close: () => void }) => (
              <AtlasSalesExportModal
                open={props.open}
                onClose={props.close}
                metricType={DorisMetricTypeEnum.ProductsWithContentScoreData}
                customDropdownOptions={[EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_CONTENT_SCORE_PRODUCT]]}
              />
            )
          }}
          indexName="sales"
          sortFieldName="rating"
          fields={[...fields].map(removeComparisonFormatter)}
          headColumnDefinition={getWeekColumnDefinition({
            getTableColumnProps(defaults) {
              return {
                ...defaults,
                style: {
                  ...defaults.style,
                  width: '220px'
                }
              };
            }
          })}
          customDataSource={{
            data: enrichedData,
            isLoading: isLoading || isMetadataLoading,
            getValuesByFieldName: getPagedValuesByFieldName,
            getValueDifferencesByFieldName: getPagedValueDifferencesByFieldName
          }}
          postProcessData={(data) => {
            return data.sort((a, b) => b.weekId - a.weekId);
          }}
          useTableDataProps={{
            groupByFieldName: 'weekId',
            filterResponse: () => true,
            itemsPerPage: 100
          }}
          showGrid={false}
        />
      ) : (
        <Flex flexDirection="column" gap={16}>
          <AtlasV2ProductListContainer
            showGrid
            indexName="sales"
            sortFieldName={DorisTableFieldColumnNamesEnum.ContentScore}
            fields={fields}
            entityTableHeaderProps={{
              title: `Content Score by Product`,
              customExportModal: (props: { open: boolean; close: () => void }) => (
                <AtlasSalesExportModal
                  open={props.open}
                  onClose={props.close}
                  metricType={DorisMetricTypeEnum.ProductsWithContentScoreData}
                  customDropdownOptions={[EXPORT_TYPES_DROPDOWN_OPTIONS[EXPORT_TYPES.GROUP_BY_CONTENT_SCORE_PRODUCT]]}
                />
              )
            }}
            headColumnDefinition={{
              headerName: 'Product',
              valueFormatter: (_, row: { product: { id: string; title: string } }) => {
                return (
                  <div className="product-col" style={{ width: '315px' }}>
                    <ProductCell stacklineSku={row.product?.id} title={row.product?.title} />
                  </div>
                );
              }
            }}
            customDataSource={{
              data: enrichedData,
              isLoading: isLoading || isMetadataLoading,
              getValuesByFieldName: getPagedValuesByFieldName,
              getValueDifferencesByFieldName: getPagedValueDifferencesByFieldName
            }}
            postProcessData={(data) => {
              return data.sort((a, b) => b.retailSales.value - a.retailSales.value);
            }}
            useTableDataProps={{
              itemsPerPage: 300
            }}
            shouldHidePagination
            iconList={[IconsList.export, IconsList.tile, IconsList.table]}
          />
          <Flex justifyContent="flex-end">
            <Pagination
              currentPage={currentPage}
              shouldUseInfinitePagination
              onClickNext={handleNextPage}
              capPage={50}
              onClickPrevious={handlePreviousPage}
            />
          </Flex>
        </Flex>
      )}
    </Box>
  );
}
